import type { EventDetail } from "@/types/event";
import { FormTiny, FormTinyWithStatus } from "@/types/forms/form";
import {
  Availability,
  AvailabilityRequestAssignment,
  AvailabilityWire,
  CancellationPolicy,
  FrontPage,
  FrontPageImages,
  GroupedAvailability,
  GroupedAvailabilityWire,
  ProviderNextAvailability,
  PublicBookingEvent,
  SelfScheduleAppointment,
  TimeOfDay,
} from "@/types/self-schedule";

import { requestData } from "@/api/api-config";

export interface PatientWaitlistRequest {
  assignments: AvailabilityRequestAssignment[];
  addonSelections?: Record<string, string[]>;
  locationId: string;
  startDate: string;
  endDate: string;
  timePreference?: TimeOfDay | null;
}

export async function joinPatientWaitlist(data: PatientWaitlistRequest) {
  const result = await requestData<{ id: string }>(
    "patient/self-schedule/waitlist",
    "post",
    { json: data },
    false,
  );
  if (!result?.id) throw new Error("Waitlist enrollment was not confirmed");
  return result;
}

export async function getFrontPage(): Promise<FrontPage | null> {
  return await requestData<FrontPage>(
    "patient/self-schedule/front-page",
    "get",
  );
}

export async function getCancellationPolicy(
  serviceIds: string[],
): Promise<CancellationPolicy | null> {
  return await requestData<CancellationPolicy>(
    "patient/self-schedule/cancellation-policy",
    "post",
    { json: { serviceIds } },
  );
}

export async function getFrontPageImages(): Promise<FrontPageImages | null> {
  return await requestData<FrontPageImages>(
    "patient/self-schedule/front-page-images",
    "get",
  );
}

export async function getBookingEvent(
  bookingEventId: string,
  reschedulingEventId?: string | null,
): Promise<PublicBookingEvent | null> {
  return await requestData<PublicBookingEvent>(
    `patient/self-schedule/booking-event/${bookingEventId}`,
    "get",
    reschedulingEventId
      ? { searchParams: { rescheduling_event_id: reschedulingEventId } }
      : undefined,
    // Let the 404 an expired or paused link returns reach the caller: the
    // booking page uses it to say so and fall back to ordinary booking.
    // Swallowed into `null` it is indistinguishable from "still loading".
    false,
  );
}

const parseAvailability = (slot: AvailabilityWire): Availability => ({
  ...slot,
  start: new Date(slot.start),
  end: new Date(slot.end),
});

/** Turns the API's ISO strings into the Dates the time picker computes with. */
const parseGroupedAvailability = (
  group: GroupedAvailabilityWire,
): GroupedAvailability => ({
  date: new Date(group.date),
  hasShift: group.hasShift,
  morning: group.morning.map(parseAvailability),
  afternoon: group.afternoon.map(parseAvailability),
  evening: group.evening.map(parseAvailability),
});

/**
 * Where to look for open times: one location, or every location that can host
 * the services, with days on `timezone`'s clock (a booking of virtual services,
 * whose patient picks no location).
 */
export type AvailabilityScope = { locationId: string } | { timezone: string };

/** Open times in `scope`. Each slot names the location it was found at. */
export async function getAvailability(
  assignments: AvailabilityRequestAssignment[],
  start: Date,
  end: Date,
  scope: AvailabilityScope,
  selectedAddons?: Record<string, string[]>,
  controller: AbortController | null = null,
  bookingEventId?: string | null,
  reschedulingEventId?: string | null,
): Promise<GroupedAvailability[] | null> {
  const groups = await requestData<GroupedAvailabilityWire[]>(
    "patient/self-schedule/availability",
    "post",
    {
      json: {
        assignments,
        ...scope,
        start: start.toISOString(),
        end: end.toISOString(),
        addonSelections:
          selectedAddons && Object.keys(selectedAddons).length > 0
            ? selectedAddons
            : undefined,
        bookingEventId: bookingEventId ?? undefined,
        reschedulingEventId: reschedulingEventId ?? undefined,
        includeShiftDates: true,
      },
      signal: controller?.signal,
    },
  );
  return groups?.map(parseGroupedAvailability) ?? null;
}

export interface ProviderNextAvailabilityRequest {
  serviceIds: string[];
  providerIds: string[];
  locationId: string;
  addonSelections?: Record<string, string[]>;
  bookingEventId?: string | null;
}

export async function getProviderNextAvailability({
  addonSelections,
  bookingEventId,
  ...request
}: ProviderNextAvailabilityRequest) {
  return requestData<ProviderNextAvailability[]>(
    "patient/self-schedule/provider-next-availability",
    "post",
    {
      json: {
        ...request,
        addonSelections:
          addonSelections && Object.keys(addonSelections).length > 0
            ? addonSelections
            : undefined,
        bookingEventId: bookingEventId ?? undefined,
      },
    },
  );
}

export async function selfScheduleAppointment(
  appointment: SelfScheduleAppointment,
): Promise<EventDetail | null> {
  return await requestData<EventDetail>(
    "patient/self-schedule/appointment",
    "post",
    {
      json: appointment,
    },
    false,
  );
}

export async function cancelAppointment(
  appointmentId: string,
  cancellationReason?: string,
) {
  const body: {
    cancellation_reason?: string;
  } = {};

  if (cancellationReason) {
    body.cancellation_reason = cancellationReason;
  }

  return await requestData<boolean>(
    `patient/self-schedule/cancel-appointment/${appointmentId}`,
    "post",
    { json: body },
    false,
  );
}

export async function getAppointmentForms(eventId: string) {
  return await requestData<FormTiny[]>(
    `patient/self-schedule/appointment/${eventId}/forms`,
    "get",
    {},
    false,
  );
}

export async function getAppointmentFormsWithStatus(
  eventId: string,
  patientId: string,
) {
  return await requestData<FormTinyWithStatus[]>(
    `patient/self-schedule/appointment/${eventId}/forms/with-status`,
    "get",
    {
      searchParams: { patient_id: patientId },
    },
    false,
  );
}

export async function appointmentFormsExist(eventId: string) {
  return await requestData<boolean>(
    `forms/appointment/${eventId}/exists`,
    "get",
  );
}

export async function checkIfPatientWithAppointments(patientId: string) {
  return await requestData<boolean>(
    `patient/self-schedule/check-if-patient-with-appointments/${patientId}`,
    "get",
  );
}

export async function checkPhoneBlacklistStatus(
  phoneNumber: string,
): Promise<boolean | null> {
  return await requestData<boolean>(
    `patient/self-schedule/blacklist/check-phone/${encodeURIComponent(phoneNumber)}`,
    "get",
  );
}
